import { ReactElement, SetStateAction, useContext, useState } from "react"
import { DashboardContext } from "../context"
import { Item, ObjectGeneric } from "../../../../../utils/types/generals"
import { TypeOfGraphs } from "../fragments/indicators/typing/types"
import { PercentageStatus } from "../fragments/indicators/typing/enums"
import { graphicData, refresh } from "../fragments/indicators/methods/callEndpoints"
import { actionPlanStatus, programmingStatus, unitStatus } from "../fragments/methods/statusByPercentage"
import { useTranslation } from "react-i18next"
import { dataIndicator } from "../components/cardIndicator"
import { FilterBody } from "../../../../../hooks/filter/typing"

type CardPayload = {
    filterData?: Record<TypeOfGraphs, ObjectGeneric>
    unitList: string[]
}
type Loaders = {
    units: boolean
    programming: boolean
    actionPlan: boolean
    table: boolean
}
type Percentage = {
    units: number
    programming: number
    actionPlan: number
}
type DatesDashboard = {
    unitDate: string
    unitWeek: string
    programmingDate: string
    programmingWeek: string
    actionPLanDate: string
    actionPLanWeek: string
}
const DashboardMainProvider = ({ children }: { children: ReactElement }) => {
    const [items, setItems] = useState<Record<string, (Item)[] | boolean>>({})
    const { t } = useTranslation()
    const [loaders, setLoaders] = useState<Loaders>({
        units: true,
        programming: true,
        actionPlan: true,
        table: true,
    })

    const [unitData, setUnitData] = useState<Record<string, dataIndicator>>({
        programmed: {
          id: 'programmed',
          amount: 0,
          color: PercentageStatus.RED,
          type: 'dark',
          percentage: '0%',
          title: t('dashboard.kpi.programmed'),
        },
        notProgrammed: {
          id: 'notProgrammed',
          amount: 0,
          color: PercentageStatus.GREEN,
          type: 'dark',
          percentage: '0%',
          title: t('dashboard.kpi.noProgrammed'),
        },
      })
    const [programmingData, setProgrammingData] = useState<
        Record<string, dataIndicator>
    >({
        paused: {
            id: 'paused',
            amount: 0,
            color: PercentageStatus.GREEN,
            type: 'dark',
            percentage: '0%',
            title: t('dashboard.kpi.inProgress'),
        },
        noStarted: {
            id: 'noStarted',
            amount: 0,
            color: PercentageStatus.GREEN,
            type: 'dark',
            percentage: '0%',
            title: t('dashboard.kpi.noStarted'),
        },
        finished: {
            id: 'finished',
            amount: 0,
            color: PercentageStatus.RED,
            type: 'dark',
            percentage: '0%',
            title: t('dashboard.kpi.finished'),
        },
    })
    const [graphicHistory, setGraphicHistory] = useState<
    {
      here?: boolean
      name: string
      programming?: number
      noProgramming?: number
      total: number
      paused?: number
      noStarted?: number
      finished?: number
      canceled?: number
    }[]
  >([])
  const [currentValueExisted, setCurrentValueExisted] = useState({
    units: false,
    programming: false,
    actionPlan: false,
  })
    const [porcentage, setPorcentage] = useState<Percentage>({
        units: 0,
        programming: 0,
        actionPlan: 0,
    })
    const [dates, setDates] = useState<DatesDashboard>({
        unitDate: '',
        unitWeek: '',
        programmingDate: '',
        programmingWeek: '',
        actionPLanDate: '',
        actionPLanWeek: '',
    })
    const [actionPlanData, setActionPlanData] = useState<
        Record<string, dataIndicator>
    >({
        noStarted: {
            id: 'noStarted',
            amount: 0,
            color: PercentageStatus.GREEN,
            type: 'dark',
            percentage: '0%',
            title: t('dashboard.kpi.noStarted'),
        },
        paused: {
            id: 'paused',
            amount: 0,
            color: PercentageStatus.GREEN,
            type: 'dark',
            percentage: '0%',
            title: t('dashboard.kpi.inProgress'),
        },
        finished: {
            id: 'finished',
            amount: 0,
            color: PercentageStatus.GREEN,
            type: 'dark',
            percentage: '0%',
            title: t('dashboard.kpi.finished'),
        },
        rejected: {
            id: 'rejected',
            amount: 0,
            color: PercentageStatus.RED,
            type: 'dark',
            percentage: '0%',
            title: t('dashboard.kpi.rejected'),
        },
        expired: {
            id: 'expired',
            amount: 0,
            color: PercentageStatus.GREEN,
            type: 'dark',
            percentage: '0%',
            title: t('dashboard.kpi.expired'),
        },
        canceled: {
            id: 'canceled',
            amount: 0,
            color: PercentageStatus.GREEN,
            type: 'dark',
            percentage: '0%',
            title: t('dashboard.kpi.canceled'),
        },
    })
    const getActionPlanData = async ({ filterData, unitList, }: CardPayload) => {
        const units = filterData?.actionPlan?.store?.length
            ? filterData?.actionPlan.store.filter((item: Item) => item.value)?.map((item: Item) => item.value)
            : (unitList)
        const divisions = filterData?.actionPlan?.divisions?.map((item: Item) => item.value)
        const zones = filterData?.actionPlan?.zones?.map((item: Item) => item.value)
        const classes = filterData?.actionPlan?.class?.map((item: Item) => item.value)
        setLoaders((prev) => ({ ...prev, actionPlan: true }))
        const response = await refresh({ units: units ?? [], classes, divisions, zones }).actionPlan()
        const colorPaused = actionPlanStatus(parseInt(response.percentagePaused)).paused()
        const colorNoStarted = actionPlanStatus(
            parseInt(response.percentageNoStarted)
        ).noStarted()
        const colorFinished = actionPlanStatus(
            parseInt(response.percentageFinished)
        ).finished()
        const colorRejected = actionPlanStatus(
            parseInt(response.percentageRejectd)
        ).canceled()
        const colorCanceled = actionPlanStatus(
            parseInt(response.percentageCanceled)
        ).canceled()
        const colorExpired = actionPlanStatus(
            parseInt(response.percentageExpired)
        ).canceled()

        setPorcentage((prev) => ({
            ...prev,
            actionPlan: response.total,
        }))

        setDates((prev) => ({
            ...prev,
            actionPLanWeek: response.semana.toString(),
            actionPLanDate: response.updateDate ?? '',
        }))
        setActionPlanData((prev) => ({
            ...prev,
            paused: {
                ...prev.paused,
                color: colorPaused,
                amount: response.paused,
                percentage: `${response.percentagePaused} %`,
            },
            canceled: {
                ...prev.canceled,
                color: colorCanceled,
                amount: response.canceled,
                percentage: `${response.percentageCanceled} %`,
            },
            expired: {
                ...prev.expired,
                color: colorExpired,
                amount: response.expired,
                percentage: `${response.percentageExpired} %`,
            },
            noStarted: {
                ...prev.noStarted,
                color: colorNoStarted,
                amount: response.noStarted,
                percentage: `${response.percentageNoStarted} %`,
            },
            rejected: {
                ...prev.rejected,
                color: colorRejected,
                amount: response.rejectd,
                percentage: `${response.percentageRejectd} %`,
            },
            finished: {
                ...prev.finished,
                color: colorFinished,
                amount: response.finished,
                percentage: `${response.percentageFinished} %`,
            },
        }))
        setLoaders((prev) => ({ ...prev, actionPlan: false }))
    }


    const getProgrammingData = async ({ filterData, unitList, }: CardPayload) => {
        const units = filterData?.programming.store?.length
            ? filterData?.programming.store.filter((item: Item) => item.value)?.map((item: Item) => item.value)
            : (unitList)
        const divisions = filterData?.programming?.divisions?.map((item: Item) => item.value)
        const zones = filterData?.programming.zones?.map((item: Item) => item.value)
        const classes = filterData?.programming?.class?.map((item: Item) => item.value)
        setLoaders((prev) => ({ ...prev, programming: true }))
        const response = await refresh({ units: units ?? [], classes, divisions, zones }).programming()
        const colorPaused = programmingStatus(parseInt(response.percentagePaused)).paused()
        const colorNoStarted = programmingStatus(
            parseInt(response.percentageNoStarted)
        ).noStarted()
        const colorFinished = programmingStatus(
            parseInt(response.percentageFinished)
        ).finished()

        setPorcentage((prev) => ({
            ...prev,
            programming: response.total,
        }))
        setDates((prev) => ({
            ...prev,
            programmingWeek: response.semana.toString(),
            programmingDate: response.updateDate ?? '',
        }))
        setProgrammingData((prev) => ({
            ...prev,
            paused: {
                ...prev.paused,
                color: colorPaused,
                amount: response.paused,
                percentage: `${response.percentagePaused} %`,
            },
            noStarted: {
                ...prev.noStarted,
                color: colorNoStarted,
                amount: response.noStarted,
                percentage: `${response.percentageNoStarted} %`,
            },
            finished: {
                ...prev.finished,
                color: colorFinished,
                amount: response.finished,
                percentage: `${response.percentageFinished} %`,
            },
        }))
        setLoaders((prev) => ({ ...prev, programming: false }))
    }


    const getUnitsData = async ({ filterData, unitList, }: CardPayload) => {
        const units = filterData?.units.store?.length
            ? filterData?.units.store.filter((item: Item) => item.value)?.map((item: Item) => item.value)
            : (unitList)

        const divisions = filterData?.units?.divisions?.map((item: Item) => item.value)
        const zones = filterData?.units.zones?.map((item: Item) => item.value)
        const classes = filterData?.units?.class?.map((item: Item) => item.value)
        setLoaders((prev) => ({ ...prev, units: true }))
        const response = await refresh({ units: units ?? [], classes, divisions, zones }).units()
        const colorProgramming = unitStatus(
            parseInt(response.percentageProgramming)
        ).programming()
        const colorNoProgramming = unitStatus(
            parseInt(response.percentageNoProgramming)
        ).noProgramming()

        setDates((prev) => ({
            ...prev,
            unitWeek: response.semana.toString(),
            unitDate: response.updateDate ?? '',
        }))
        setPorcentage((prev) => ({
            ...prev,
            units: response.total,
        }))

        setUnitData((prev) => ({
            ...prev,
            programmed: {
                ...prev.programmed,
                color: colorProgramming,
                amount: response.programming,
                percentage: `${response.percentageProgramming} %`,
            },
            notProgrammed: {
                ...prev.notProgrammed,
                color: colorNoProgramming,
                amount: response.noProgramming,
                percentage: `${response.percentageNoProgramming} %`,
            },
        }))
        setLoaders((prev) => ({ ...prev, units: false }))
    }

    
  const handleTableLoading = ({ value }: { value: boolean }) => {
    setLoaders((prev) => ({ ...prev, table: value }))
  }
  const getHistory = async ({
    name,
    unitList,
    filterData,
    filterByHistory
  }: {
    name: 'units' | 'programming' | 'actionPlan'
    filterByHistory: {
        units: FilterBody;
        programming: FilterBody;
        actionPlan: FilterBody;
    }
  } & CardPayload) => {
    const units = filterData?.[name]?.store?.length
      ? filterData?.[name].store.map((item: Item) => item.value)
      : (unitList)

    const divisions = filterData?.[name]?.divisions?.map((item: Item) => item.value)
    const zones = filterData?.[name]?.zones?.map((item: Item) => item.value)
    const classes = filterData?.[name]?.class?.map((item: Item) => item.value)

    handleTableLoading({ value: true })
    const { data, currentValueExisted } = await graphicData({
      units: units ?? [],
      classes,
      divisions,
      zones,
      t
    })[name]({
      interval: filterByHistory[name].filterData?.intervale?.value,
      date: {
        start: filterByHistory[name].filterData.from,
        end: filterByHistory[name].filterData.to,
      },
    })
    setGraphicHistory(data)
    setCurrentValueExisted((prev) => ({
      ...prev,
      [name]: currentValueExisted,
    }))
    handleTableLoading({ value: false })
  }

    return (
        <DashboardContext.Provider
            value={{
                items,
                dates,
                programmingData,
                unitData,
                porcentage,
                actionPlanData,
                currentValueExisted,
                loaders,
                graphicHistory,
                getHistory,
                getUnitsData,
                setLoaders,
                setItems,
                getActionPlanData,
                getProgrammingData
            }}
        >{children}</DashboardContext.Provider>
    )
}

const useDashboardMainContext = () => useContext(DashboardContext)
export type { CardPayload, Loaders, Percentage, DatesDashboard }
export { DashboardMainProvider, useDashboardMainContext }